import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, XCircle, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function fmtDT(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return iso; }
}
function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function ZeitBestaetigen() {
  const { token } = useParams();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [disputeMode, setDisputeMode] = useState(false);
  const [disputeMsg, setDisputeMsg] = useState("");
  const [done, setDone] = useState(null); // "confirmed" | "disputed"

  useEffect(() => {
    axios
      .get(`${API}/time-entries/by-token/${token}`)
      .then(({ data }) => setEntry(data))
      .catch((err) => setError(err.response?.data?.detail || "Zeit-Eintrag nicht gefunden"))
      .finally(() => setLoading(false));
  }, [token]);

  const confirm = async () => {
    if (!window.confirm("Erfasste Stunden verbindlich bestätigen?")) return;
    setSending(true);
    try {
      await axios.post(`${API}/time-entries/by-token/${token}/confirm`, { ok: true });
      setDone("confirmed");
      toast.success("Bestätigt — vielen Dank!");
    } catch (err) {
      toast.error("Fehler: " + (err.response?.data?.detail || err.message));
    } finally { setSending(false); }
  };

  const submitDispute = async () => {
    if (!disputeMsg.trim()) { toast.error("Bitte kurz beschreiben, was nicht stimmt"); return; }
    setSending(true);
    try {
      await axios.post(`${API}/time-entries/by-token/${token}/confirm`, { ok: false, message: disputeMsg });
      setDone("disputed");
      toast.success("Rückfrage übermittelt — Lukas meldet sich zurück");
    } catch (err) {
      toast.error("Fehler: " + (err.response?.data?.detail || err.message));
    } finally { setSending(false); }
  };

  if (loading) {
    return <div className="min-h-[70vh] flex items-center justify-center"><Loader2 className="animate-spin text-[#BA6D52]" /></div>;
  }
  if (error || !entry) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <XCircle className="w-12 h-12 text-red-700 mx-auto mb-4" />
        <h1 className="font-serif text-3xl">Zeit-Eintrag nicht verfügbar</h1>
        <p className="text-[#757067] mt-3">{error || "Der Link ist ungültig oder abgelaufen."}</p>
      </div>
    );
  }

  const rc = entry.rate_card || {};
  const net = (Number(entry.hours || 0) * Number(entry.hourly_rate_eur || 0));
  const canRespond = entry.status === "pending_customer" && !done;

  return (
    <div className="max-w-3xl mx-auto px-6 py-16 md:py-24" data-testid="zeit-bestaetigen-page">
      <p className="text-[10px] uppercase tracking-[0.35em] text-[#BA6D52]">Arbeitsstunden — Bestätigung</p>
      <h1 className="font-serif text-4xl md:text-5xl mt-3">{rc.betrieb || "—"}</h1>
      {rc.quote_number && (
        <p className="text-[#757067] mt-3">zu Stunden-Angebot <b>{rc.quote_number}</b></p>
      )}

      <div className="mt-10 border border-[#E3DECE] p-6 md:p-8 bg-[#FAFAF7] space-y-3" data-testid="zeit-detail">
        <div className="flex justify-between text-sm">
          <span className="text-[#757067]">Datum</span>
          <b>{fmtDate(entry.started_at)}</b>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-[#757067]">Zeitraum</span>
          <b>{fmtTime(entry.started_at)} – {fmtTime(entry.ended_at)} Uhr</b>
        </div>
        <div className="flex justify-between text-sm border-t border-[#E3DECE] pt-3">
          <span className="text-[#757067]">Erfasste Stunden</span>
          <b className="font-serif text-2xl text-[#BA6D52]">{Number(entry.hours || 0).toFixed(2).replace(".", ",")} h</b>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-[#757067]">Stundensatz</span>
          <b>{Number(entry.hourly_rate_eur || 0).toFixed(2).replace(".", ",")} € netto / h</b>
        </div>
        <div className="flex justify-between text-sm border-t border-[#E3DECE] pt-3">
          <span className="text-[#757067]">Zwischensumme netto</span>
          <b>{net.toFixed(2).replace(".", ",")} €</b>
        </div>
        {entry.notes && (
          <div className="border-t border-[#E3DECE] pt-3">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA6D52] mb-1">Notiz</p>
            <p className="text-sm whitespace-pre-line">{entry.notes}</p>
          </div>
        )}
      </div>

      {done === "confirmed" || entry.status === "confirmed" || entry.status === "invoiced" ? (
        <div className="mt-10 text-center" data-testid="zeit-confirmed">
          <CheckCircle2 className="w-16 h-16 text-emerald-700 mx-auto mb-4" />
          <h2 className="font-serif text-2xl">Bestätigt — vielen Dank!</h2>
          <p className="text-[#757067] mt-3 max-w-lg mx-auto">
            Die Stunden wurden zu Ihrer Gesamtabrechnung hinzugefügt. Sie erhalten die Rechnung, sobald Lukas alle Einsätze abschließt.
          </p>
        </div>
      ) : done === "disputed" || entry.status === "disputed" ? (
        <div className="mt-10 text-center">
          <MessageSquare className="w-16 h-16 text-amber-700 mx-auto mb-4" />
          <h2 className="font-serif text-2xl">Rückfrage übermittelt</h2>
          <p className="text-[#757067] mt-3 max-w-lg mx-auto">
            Lukas hat Ihre Nachricht erhalten und meldet sich zurück, um die Details persönlich zu klären.
          </p>
        </div>
      ) : canRespond ? (
        <div className="mt-10 border-t border-[#E3DECE] pt-8">
          {disputeMode ? (
            <>
              <label className="text-xs uppercase tracking-[0.2em] text-[#BA6D52]">Was stimmt nicht?</label>
              <textarea
                rows={4}
                value={disputeMsg}
                onChange={(e) => setDisputeMsg(e.target.value)}
                placeholder="z. B. richtige Endzeit war 14:30, nicht 15:00 …"
                className="mt-2 w-full bg-[#FAFAF7] border border-[#E3DECE] focus:outline-none focus:border-[#BA6D52] p-3 text-sm"
                data-testid="zeit-dispute-msg"
              />
              <div className="mt-6 flex flex-col md:flex-row gap-3 justify-end">
                <button
                  onClick={() => setDisputeMode(false)}
                  disabled={sending}
                  className="border border-[#E3DECE] hover:border-[#BA6D52] px-6 py-3 text-xs uppercase tracking-[0.2em]"
                >
                  Zurück
                </button>
                <button
                  onClick={submitDispute}
                  disabled={sending || !disputeMsg.trim()}
                  data-testid="zeit-dispute-submit"
                  className="bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-60 px-6 py-3 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2"
                >
                  {sending && <Loader2 size={14} className="animate-spin" />}
                  Rückfrage senden
                </button>
              </div>
            </>
          ) : (
            <div className="flex flex-col md:flex-row gap-3 justify-end">
              <button
                onClick={() => setDisputeMode(true)}
                disabled={sending}
                data-testid="zeit-dispute-open"
                className="border border-[#E3DECE] hover:border-amber-700 hover:text-amber-700 px-6 py-3 text-xs uppercase tracking-[0.2em]"
              >
                Rückfrage / Korrektur
              </button>
              <button
                onClick={confirm}
                disabled={sending}
                data-testid="zeit-confirm"
                className="bg-[#BA6D52] text-white hover:bg-[#9E5841] disabled:opacity-60 px-8 py-3 text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2"
              >
                {sending && <Loader2 size={14} className="animate-spin" />}
                Stunden bestätigen
              </button>
            </div>
          )}
          <p className="text-xs text-[#757067] mt-4 text-center">
            Erfasst am {fmtDT(entry.confirmation_sent_at)} durch Lukas Stutz. Es gelten die <a href="/agb" className="text-[#BA6D52] underline">AGB</a>.
          </p>
        </div>
      ) : (
        <div className="mt-10 text-center text-[#757067] text-sm">
          Status: <b>{entry.status}</b>
        </div>
      )}
    </div>
  );
}
